import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Users, UserPlus, Activity, Heart, MessageSquare, Flag,
  Download, Calendar, ChevronDown, RefreshCw, Clock,
  Eye, Search, X, Sparkles, TrendingUp, MapPin, ArrowRight,
  Coins, CreditCard, Crown, DollarSign, Flame, Wallet, ExternalLink,
  Receipt, ChevronLeft, ChevronRight, CheckCircle2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import AdminService from "../../sevices/admin.service";

const AGE_COLORS = ["#8b5cf6", "#3b82f6", "#06b6d4", "#f59e0b", "#ec4899", "#64748b"];
const GENDER_COLORS = ["#ec4899", "#3b82f6", "#8b5cf6"];
const FEATURE_PALETTE = ["#f43f5e", "#8b5cf6", "#f59e0b", "#06b6d4", "#ec4899", "#3b82f6", "#10b981", "#64748b"];

const CustomCurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-xl px-4 py-3 text-xs font-semibold z-50 animate-in fade-in duration-150">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 my-1">
          <span className="flex items-center gap-2 text-gray-700">
            <span className="w-2.5 h-2.5 rounded-full shadow-xs" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
            <span className="capitalize">{entry.name}:</span>
          </span>
          <span className="font-bold text-gray-900 tabular-nums">
            CA$ {Number(entry.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
};

const CustomCoinsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-xl px-4 py-3 text-xs font-semibold z-50 animate-in fade-in duration-150">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((entry, index) => {
        const val = Number(entry.value || 0);
        const cadVal = (val * 0.125).toFixed(2);
        return (
          <div key={index} className="flex items-center justify-between gap-4 my-1">
            <span className="flex items-center gap-2 text-gray-700">
              <span className="w-2.5 h-2.5 rounded-full shadow-xs" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
              <span className="capitalize">{entry.name}:</span>
            </span>
            <div className="text-right">
              <span className="font-bold text-gray-900 tabular-nums">
                {val.toLocaleString()} coins
              </span>
              <span className="text-gray-400 text-[11px] font-normal ml-1.5">
                (CA$ {cadVal})
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CustomSubscriptionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-xl px-4 py-3 text-xs font-semibold z-50 animate-in fade-in duration-150">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((entry, index) => {
        const val = Number(entry.value || 0);
        const isRevenue = entry.dataKey === "subRevenue" || entry.dataKey === "cumulativeRevenue";
        return (
          <div key={index} className="flex items-center justify-between gap-4 my-1">
            <span className="flex items-center gap-2 text-gray-700">
              <span className="w-2.5 h-2.5 rounded-full shadow-xs" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
              <span className="capitalize">{entry.name}:</span>
            </span>
            <span className="font-bold text-gray-900 tabular-nums">
              {isRevenue
                ? `CA$ ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `${val.toLocaleString()} ${entry.name.toLowerCase().includes("active") ? "active" : "orders"}`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const INDIVIDUAL_GRAPH_TIME_OPTIONS = [
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
  { id: "90d", label: "90D" },
  { id: "1y", label: "1Y" },
];

const DURATION_OPTIONS = [
  { id: "7d", label: "Last 7 Days" },
  { id: "30d", label: "Last 30 Days" },
  { id: "90d", label: "Last 90 Days" },
  { id: "1y", label: "Last Year" },
  { id: "custom", label: "Custom Range..." },
];

const formatCurrencySymbol = (currency = "CAD") => {
  switch ((currency || "CAD").toUpperCase()) {
    case "CAD": return "CA$ ";
    case "INR": return "₹ ";
    case "USD": return "$ ";
    case "EUR": return "€ ";
    case "GBP": return "£ ";
    case "AUD": return "A$ ";
    case "JPY": return "¥ ";
    case "SGD": return "S$ ";
    case "AED": return "AED ";
    default: return `${currency} `;
  }
};

const COUNTRY_FLAGS = {
  CANADA: "🇨🇦",
  INDIA: "🇮🇳",
  UNITED_STATES: "🇺🇸",
  USA: "🇺🇸",
  UNITED_KINGDOM: "🇬🇧",
  UK: "🇬🇧",
  AUSTRALIA: "🇦🇺",
  GERMANY: "🇩🇪",
  FRANCE: "🇫🇷",
  UNITED_ARAB_EMIRATES: "🇦🇪",
  UAE: "🇦🇪",
  SINGAPORE: "🇸🇬",
  PHILIPPINES: "🇵🇭",
  PAKISTAN: "🇵🇰",
  BANGLADESH: "🇧🇩",
  BRAZIL: "🇧🇷",
  SPAIN: "🇪🇸",
  ITALY: "🇮🇹",
  NETHERLANDS: "🇳🇱",
  JAPAN: "🇯🇵",
  SOUTH_KOREA: "🇰🇷",
  MEXICO: "🇲🇽",
  ARGENTINA: "🇦🇷",
  NEW_ZEALAND: "🇳🇿",
  IRELAND: "🇮🇪",
  SWITZERLAND: "🇨🇭",
  SWEDEN: "🇸🇪",
  NORWAY: "🇳🇴",
  DENMARK: "🇩🇰",
  SOUTH_AFRICA: "🇿🇦",
  AUSTRIA: "🇦🇹",
  POLAND: "🇵🇱",
  PORTUGAL: "🇵🇹",
  THAILAND: "🇹🇭",
  INDONESIA: "🇮🇩",
  MALAYSIA: "🇲🇾",
  VIETNAM: "🇻🇳",
  SAUDI_ARABIA: "🇸🇦",
  QATAR: "🇶🇦",
  KUWAIT: "🇰🇼",
  EGYPT: "🇪🇬",
  NIGERIA: "🇳🇬",
  KENYA: "🇰🇪",
  TURKEY: "🇹🇷",
  GREECE: "🇬🇷",
  AMERICAN_SAMOA: "🇦🇸",
  ANDORRA: "🇦🇩",
  ALAND_ISLANDS: "🇦🇽",
  ALGERIA: "🇩🇿",
  ANGUILLA: "🇦🇮",
};

const POPULAR_COUNTRIES = [
  "Canada",
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "UAE",
  "Germany",
  "France",
  "Singapore",
  "Brazil",
  "Spain",
  "Italy",
  "Netherlands",
  "Japan",
  "South Korea",
  "Mexico",
  "Argentina",
  "New Zealand",
  "Ireland",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "South Africa",
  "Poland",
  "Portugal",
  "Thailand",
  "Indonesia",
  "Malaysia",
  "Philippines",
  "Vietnam",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "Egypt",
  "Nigeria",
  "Kenya",
  "Turkey",
  "Greece"
];

const getCountryFlag = (country) => {
  if (!country || country === "ALL") return "🌐";
  const normalized = country.toUpperCase().trim().replace(/[\s-]+/g, "_");
  if (COUNTRY_FLAGS[normalized]) return COUNTRY_FLAGS[normalized];
  for (const [key, flag] of Object.entries(COUNTRY_FLAGS)) {
    if (normalized.includes(key) || key.includes(normalized)) return flag;
  }
  return "🌐";
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-xl px-4 py-3 text-xs font-semibold z-50 animate-in fade-in duration-150">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 my-1">
          <span className="flex items-center gap-2 text-gray-700">
            <span className="w-2.5 h-2.5 rounded-full shadow-xs" style={{ backgroundColor: entry.color || entry.stroke || entry.fill }} />
            <span className="capitalize">{entry.name}:</span>
          </span>
          <span className="font-bold text-gray-900 tabular-nums">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const CustomDemographicsTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-xl px-4 py-3 text-xs font-semibold z-50 animate-in fade-in zoom-in-95 duration-150">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: payload[0].color || payload[0].fill }} />
        <span className="text-gray-900 font-bold">{item.label}</span>
      </div>
      <div className="text-gray-600 font-medium space-y-1">
        <p className="flex justify-between gap-3">
          <span className="text-gray-400">Users:</span>
          <span className="text-gray-900 font-bold tabular-nums">{item.count?.toLocaleString() || 0}</span>
        </p>
        <p className="flex justify-between gap-3">
          <span className="text-gray-400">Share:</span>
          <span className="text-purple-600 font-bold tabular-nums">{item.percentage}%</span>
        </p>
      </div>
    </div>
  );
};

const MonetizationLoadingSkeleton = () => (
  <div className="space-y-8 animate-in fade-in duration-300">
    {/* Top Sync Banner */}
    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-emerald-500/10 border border-amber-200/50 rounded-3xl animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-600">
          <RefreshCw className="w-4 h-4 animate-spin" />
        </div>
        <div>
          <p className="text-xs font-bold text-gray-900">Loading Revenue & Economy Ledger...</p>
          <p className="text-[11px] text-gray-500">Aggregating coin purchases, Stripe subscriptions, and conversion rates</p>
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/80 rounded-full border border-amber-200/40">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Syncing</span>
      </div>
    </div>

    {/* Top 6 KPI Skeletons */}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between animate-pulse">
          <div className="w-10 h-10 rounded-2xl bg-gray-100 mb-4" />
          <div className="space-y-2">
            <div className="h-3 w-16 bg-gray-100 rounded-md" />
            <div className="h-7 w-24 bg-gray-200 rounded-lg" />
            <div className="h-2.5 w-20 bg-gray-100 rounded-md" />
          </div>
          <div className="h-4 w-14 bg-gray-100 rounded-full mt-4" />
        </div>
      ))}
    </div>

    {/* Revenue Streams Trend Skeleton */}
    <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="space-y-2">
          <div className="h-4 w-44 bg-gray-200 rounded-md" />
          <div className="h-3 w-64 bg-gray-100 rounded-md" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-3 w-16 bg-gray-100 rounded-md" />
          <div className="h-3 w-16 bg-gray-100 rounded-md" />
          <div className="h-3 w-20 bg-gray-100 rounded-md" />
        </div>
      </div>
      <div className="h-[280px] w-full bg-gradient-to-b from-gray-50/80 to-transparent rounded-2xl flex items-center justify-center">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 shadow-xs rounded-xl border border-gray-200/60 text-xs font-semibold text-gray-400">
          <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-600" />
          <span>Generating Revenue Trend Chart...</span>
        </div>
      </div>
    </div>

    {/* 2-Column Graphs Grid Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Coin Economy Flow Skeleton */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="space-y-2">
            <div className="h-4 w-40 bg-gray-200 rounded-md" />
            <div className="h-3 w-52 bg-gray-100 rounded-md" />
          </div>
          <div className="h-6 w-28 bg-gray-100 rounded-full" />
        </div>
        <div className="h-[320px] w-full bg-gradient-to-b from-amber-50/30 via-gray-50/50 to-transparent rounded-2xl flex items-end justify-around px-6 pb-6 pt-12">
          {[40, 70, 55, 90, 65, 80, 50, 75].map((h, idx) => (
            <div key={idx} className="flex items-end gap-1.5 h-full">
              <div className="w-3 sm:w-4 bg-emerald-200/60 rounded-t-md" style={{ height: `${h}%` }} />
              <div className="w-3 sm:w-4 bg-rose-200/60 rounded-t-md" style={{ height: `${Math.round(h * 0.55)}%` }} />
            </div>
          ))}
        </div>
      </div>

      {/* Subscription Retention Skeleton */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="space-y-2">
            <div className="h-4 w-40 bg-gray-200 rounded-md" />
            <div className="h-3 w-52 bg-gray-100 rounded-md" />
          </div>
          <div className="h-6 w-28 bg-gray-100 rounded-full" />
        </div>
        <div className="h-[320px] w-full bg-gradient-to-b from-purple-50/30 via-gray-50/50 to-transparent rounded-2xl flex items-center justify-center">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 shadow-xs rounded-xl border border-gray-200/60 text-xs font-semibold text-gray-400">
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" />
            <span>Loading Membership Trends...</span>
          </div>
        </div>
      </div>
    </div>

    {/* 2 Breakdown Cards Grid Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
        <div className="h-4 w-36 bg-gray-200 rounded-md" />
        <div className="h-[220px] bg-gray-50 rounded-2xl flex items-center justify-center">
          <div className="w-20 h-20 rounded-full border-4 border-gray-200/70 border-t-amber-400 animate-spin" />
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
        <div className="h-4 w-44 bg-gray-200 rounded-md" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-50 rounded-2xl p-4 space-y-2">
              <div className="h-3 w-20 bg-gray-200 rounded-md" />
              <div className="h-4 w-28 bg-gray-100 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Purchase & Transaction Logs Table Skeleton */}
    <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6 animate-pulse">
      {/* Top 4 Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-3.5 rounded-2xl bg-gray-50 flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-200" />
            <div className="space-y-1.5 flex-1">
              <div className="h-2.5 w-16 bg-gray-200 rounded-md" />
              <div className="h-4 w-24 bg-gray-200 rounded-md" />
            </div>
          </div>
        ))}
      </div>
      {/* Search & Rows Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="h-10 w-full sm:w-72 bg-gray-100 rounded-2xl" />
        <div className="h-8 w-24 bg-gray-100 rounded-xl" />
      </div>
      {/* Table Rows Skeleton */}
      <div className="space-y-3 pt-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
              <div className="space-y-1">
                <div className="h-3.5 w-28 bg-gray-200 rounded-md" />
                <div className="h-2.5 w-36 bg-gray-100 rounded-md" />
              </div>
            </div>
            <div className="h-6 w-20 bg-gray-100 rounded-full" />
            <div className="h-4 w-16 bg-gray-100 rounded-md" />
            <div className="h-5 w-20 bg-gray-200 rounded-md" />
            <div className="h-4 w-16 bg-gray-100 rounded-md" />
            <div className="h-3 w-20 bg-gray-100 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Analytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(() => {
    try {
      const cached = sessionStorage.getItem("signova-analytics-cache");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Filter state
  const [timeRange, setTimeRange] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  // Drawers state
  const [showCitiesDrawer, setShowCitiesDrawer] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [showEngagementDrawer, setShowEngagementDrawer] = useState(false);

  // Sub-tabs state (Option A)
  const [activeTab, setActiveTab] = useState("engagement"); // "engagement" | "monetization"
  const [monetizationTabLoading, setMonetizationTabLoading] = useState(false);
  const [monetizationLoaded, setMonetizationLoaded] = useState(false);

  // Dedicated Purchase & Transaction Logs State
  const [logsData, setLogsData] = useState({
    items: [],
    page: 0,
    size: 15,
    totalElements: 0,
    totalPages: 0,
    summary: {
      totalRevenueCad: 0,
      coinPurchasesCount: 0,
      subscriptionOrdersCount: 0,
      coinSpendsCount: 0,
      totalCoinsPurchased: 0,
      totalCoinsBurned: 0,
    },
  });
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsCategory, setLogsCategory] = useState("ALL");
  const [logsCountry, setLogsCountry] = useState("ALL");
  const [logsSearch, setLogsSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [logsPage, setLogsPage] = useState(0);
  const [logsSize, setLogsSize] = useState(15);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
  const [countryFilterSearch, setCountryFilterSearch] = useState("");
  const countryDropdownRef = useRef(null);
  const rowsDropdownRef = useRef(null);

  // Individual Graph Time Range State (Task 5)
  const [coinsTimeRange, setCoinsTimeRange] = useState("30d");
  const [coinsGraphLoading, setCoinsGraphLoading] = useState(false);
  const [coinsGraphData, setCoinsGraphData] = useState(null);

  const [subsTimeRange, setSubsTimeRange] = useState("30d");
  const [subsGraphLoading, setSubsGraphLoading] = useState(false);
  const [subsGraphData, setSubsGraphData] = useState(null);

  const fetchCoinsGraph = useCallback((range) => {
    setCoinsGraphLoading(true);
    return AdminService.getCoinsGraph(range)
      .then((res) => {
        if (res?.data?.data) {
          setCoinsGraphData(res.data.data);
        }
        return res?.data?.data;
      })
      .catch((err) => {
        console.error("Failed to fetch coins graph:", err);
        return null;
      })
      .finally(() => setCoinsGraphLoading(false));
  }, []);

  const fetchSubsGraph = useCallback((range) => {
    setSubsGraphLoading(true);
    return AdminService.getSubscriptionGraph(range)
      .then((res) => {
        if (res?.data?.data) {
          setSubsGraphData(res.data.data);
        }
        return res?.data?.data;
      })
      .catch((err) => {
        console.error("Failed to fetch subs graph:", err);
        return null;
      })
      .finally(() => setSubsGraphLoading(false));
  }, []);

  const handleCoinsTimeRangeChange = (range) => {
    setCoinsTimeRange(range);
    fetchCoinsGraph(range);
  };

  const handleSubsTimeRangeChange = (range) => {
    setSubsTimeRange(range);
    fetchSubsGraph(range);
  };

  const handleTabSwitch = (newTab) => {
    if (newTab === activeTab) return;
    if (newTab === "monetization") {
      setMonetizationTabLoading(true);
    }
    setActiveTab(newTab);
  };

  const filterRef = useRef(null);

  const fetchData = useCallback((isManual = false, range = timeRange, sDate = customStart, eDate = customEnd) => {
    if (isManual) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    AdminService.getAnalytics(range, sDate, eDate)
      .then((res) => {
        const fresh = res?.data?.data;
        if (fresh) {
          setData(fresh);
          setLastUpdated(new Date());
          try {
            sessionStorage.setItem("signova-analytics-cache", JSON.stringify(fresh));
          } catch (e) { }
        }
      })
      .catch((err) => console.error("Failed to fetch analytics:", err))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [timeRange, customStart, customEnd]);

  // Search debounce for transaction logs
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(logsSearch);
      setLogsPage(0);
    }, 350);
    return () => clearTimeout(handler);
  }, [logsSearch]);

  const fetchLogs = useCallback(
    (page = logsPage, size = logsSize, cat = logsCategory, search = debouncedSearch, range = timeRange, sDate = customStart, eDate = customEnd, country = logsCountry) => {
      setLogsLoading(true);
      return AdminService.getTransactionLogs(page, size, cat, search, range, sDate, eDate, country)
        .then((res) => {
          if (res?.data?.data) {
            setLogsData(res.data.data);
          }
          return res?.data?.data;
        })
        .catch((err) => {
          console.error("Failed to fetch transaction logs:", err);
          return null;
        })
        .finally(() => setLogsLoading(false));
    },
    [logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry]
  );

  // Initial & Tab-switch loading for Monetization tab
  useEffect(() => {
    if (activeTab === "monetization") {
      setMonetizationTabLoading(true);
      const startTime = Date.now();
      Promise.allSettled([
        fetchCoinsGraph(coinsTimeRange),
        fetchSubsGraph(subsTimeRange),
        fetchLogs(logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry)
      ]).finally(() => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 600 - elapsed);
        setTimeout(() => {
          setMonetizationTabLoading(false);
          setMonetizationLoaded(true);
        }, delay);
      });
    }
  }, [activeTab, fetchCoinsGraph, fetchSubsGraph, fetchLogs, coinsTimeRange, subsTimeRange, logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry]);

  // Subsequent transaction logs updates (pagination, search, category filter, country filter)
  useEffect(() => {
    if (activeTab === "monetization" && monetizationLoaded && !monetizationTabLoading) {
      fetchLogs(logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry);
    }
  }, [logsPage, logsSize, logsCategory, debouncedSearch, logsCountry, activeTab, monetizationLoaded, monetizationTabLoading, fetchLogs, timeRange, customStart, customEnd]);

  useEffect(() => {
    fetchData(false, timeRange, customStart, customEnd);
    const interval = setInterval(() => {
      fetchData(false, timeRange, customStart, customEnd);
      if (activeTab === "monetization") {
        fetchLogs(logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry);
        fetchCoinsGraph(coinsTimeRange);
        fetchSubsGraph(subsTimeRange);
      }
    }, 15 * 60 * 1000); // 15 minutes = 900,000ms

    return () => clearInterval(interval);
  }, [fetchData, timeRange, customStart, customEnd, activeTab, fetchLogs, logsPage, logsSize, logsCategory, debouncedSearch, logsCountry, fetchCoinsGraph, fetchSubsGraph, coinsTimeRange, subsTimeRange]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsTimeDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setIsCountryDropdownOpen(false);
        setCountryFilterSearch("");
      }
      if (rowsDropdownRef.current && !rowsDropdownRef.current.contains(e.target)) {
        setIsRowsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectTimeRange = (rangeId) => {
    setIsTimeDropdownOpen(false);
    if (rangeId === "custom") {
      setShowCustomModal(true);
    } else {
      setTimeRange(rangeId);
      setCustomStart("");
      setCustomEnd("");
    }
  };

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      setTimeRange("custom");
      setShowCustomModal(false);
    }
  };

  // Format statistics strictly from real backend response data
  const totalUsers = data?.totalUsers ?? 0;
  const totalUsersGrowth = data?.totalUsersGrowth ?? 0;
  const newUsers = data?.newUsers7d ?? data?.newUsers ?? 0;
  const newUsersGrowth = data?.newUsersGrowth ?? 0;
  const activeUsers7d = data?.activeUsers7d ?? 0;
  const activeUsers7dGrowth = data?.activeUsers7dGrowth ?? 0;
  const matches7d = data?.matches7d ?? 0;
  const matches7dGrowth = data?.matches7dGrowth ?? 0;
  const messagesSent7d = data?.messagesSent7d ?? 0;
  const messagesSent7dGrowth = data?.messagesSent7dGrowth ?? 0;
  const reports7d = data?.reports7d ?? 0;
  const reports7dGrowth = data?.reports7dGrowth ?? 0;

  const growthChartData = data?.userGrowthSeries || [];
  const activityChartData = data?.userActivitySeries || [];
  const ageData = data?.ageDemographics || [];
  const genderData = data?.genderDemographics || [];
  const topCities = data?.topCities || [];
  const allCities = data?.allCities || [];

  const filteredCities = allCities.filter(c =>
    (c.city || "").toLowerCase().includes(citySearch.toLowerCase()) ||
    (c.country || "").toLowerCase().includes(citySearch.toLowerCase())
  );

  const engagement = data?.engagementOverview || {
    profileViews7d: 0,
    profileViewsGrowth: 0,
    likesSent7d: 0,
    likesSentGrowth: 0,
    matches7d: 0,
    matchesGrowth: 0,
    messagesSent7d: 0,
    messagesSentGrowth: 0,
  };

  // Monetization & Economy data
  const totalRevenue = data?.totalRevenue ?? 0;
  const totalRevenueGrowth = data?.totalRevenueGrowth ?? 0;
  const coinRevenue = data?.coinRevenue ?? 0;
  const coinRevenueGrowth = data?.coinRevenueGrowth ?? 0;
  const subscriptionRevenue = data?.subscriptionRevenue ?? 0;
  const subscriptionRevenueGrowth = data?.subscriptionRevenueGrowth ?? 0;
  const activeSubscribers = data?.activeSubscribers ?? 0;
  const activeSubscribersGrowth = data?.activeSubscribersGrowth ?? 0;
  const totalCoinsPurchased = data?.totalCoinsPurchased ?? 0;
  const totalCoinsBurned = data?.totalCoinsBurned ?? 0;
  const totalCoinsBurnedCad = data?.totalCoinsBurnedCad ?? (totalCoinsBurned * 0.125);
  const totalCoinsBurnedGrowth = data?.totalCoinsBurnedGrowth ?? 0;
  const arpu = data?.arpu ?? 0;
  const freeToPaidRate = data?.freeToPaidRate ?? 0;
  const payingUsersCount = data?.payingUsersCount ?? 0;

  const revenueTrendSeries = data?.revenueTrendSeries || [];
  const coinsEconomySeries = data?.coinsEconomySeries || [];
  const subscriptionTrendSeries = data?.subscriptionTrendSeries || [];
  const coinSpendBreakdown = data?.coinSpendBreakdown || [];
  const subscriptionPlanBreakdown = data?.subscriptionPlanBreakdown || [];
  const subscriptionStatusBreakdown = data?.subscriptionStatusBreakdown || [];

  // Active individual graph data with fallback to overall series
  const activeCoinsSeries = coinsGraphData?.series || coinsEconomySeries;
  const coinsSummary = coinsGraphData?.summary;
  const activeCoinsPurchased = coinsSummary?.totalCoinsPurchased ?? totalCoinsPurchased;
  const activeCoinsBurned = coinsSummary?.totalCoinsBurned ?? totalCoinsBurned;
  const activeCoinsBurnedCad = coinsSummary?.totalCoinsBurnedCad ?? totalCoinsBurnedCad;
  const activeCoinsPurchasedCad = coinsSummary?.totalCoinsPurchasedCad ?? (activeCoinsPurchased * 0.125);

  const activeSubsSeries = subsGraphData?.series || subscriptionTrendSeries;
  const subsSummary = subsGraphData?.summary;
  const activeSubsRevenue = subsSummary?.totalSubRevenue ?? subscriptionRevenue;
  const activeNewSubscribers = subsSummary?.newSubscribersCount ?? 0;
  const activeTotalSubscribers = subsSummary?.activeSubscribers ?? activeSubscribers;

  const selectedDurationLabel = timeRange === "custom" && customStart && customEnd
    ? `${customStart} - ${customEnd}`
    : DURATION_OPTIONS.find(d => d.id === timeRange)?.label || "Last 30 Days";

  // Extract countries that actually have transactions in the loaded dataset
  const activeTransactionCountries = useMemo(() => {
    const list = new Set();
    (logsData?.summary?.availableCountries || []).forEach(c => c && list.add(c));
    (logsData?.items || []).forEach(item => {
      if (item.country) list.add(item.country);
    });
    return Array.from(list).filter(c => c && c !== "Unknown" && c !== "ALL").sort();
  }, [logsData]);

  // Combined country list: active transaction countries + backend list + popular platform countries
  const availableCountriesList = useMemo(() => {
    const backendCountries = logsData?.summary?.availableCountries || [];
    const combined = new Set([
      ...activeTransactionCountries,
      ...backendCountries,
      ...POPULAR_COUNTRIES
    ]);
    return Array.from(combined)
      .filter(c => c && c !== "Unknown" && c !== "ALL")
      .sort((a, b) => a.localeCompare(b));
  }, [activeTransactionCountries, logsData]);

  // Countries filtered by search in dropdown
  const displayedCountries = useMemo(() => {
    if (!countryFilterSearch.trim()) return availableCountriesList;
    const q = countryFilterSearch.trim().toLowerCase();
    return availableCountriesList.filter(c => c.toLowerCase().includes(q));
  }, [availableCountriesList, countryFilterSearch]);

  const isMonetizationInitialLoading =
    activeTab === "monetization" && (monetizationTabLoading || !monetizationLoaded);

  return (
    <div className="min-h-screen bg-gray-50/60 pb-24 font-sans text-gray-800">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Analytics Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time performance metrics and user engagement insights across Signova.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              fetchData(true, timeRange, customStart, customEnd);
              if (activeTab === "monetization") {
                setMonetizationTabLoading(true);
                const startTime = Date.now();
                Promise.allSettled([
                  fetchLogs(logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry),
                  fetchCoinsGraph(coinsTimeRange),
                  fetchSubsGraph(subsTimeRange)
                ]).finally(() => {
                  const elapsed = Date.now() - startTime;
                  const delay = Math.max(0, 600 - elapsed);
                  setTimeout(() => {
                    setMonetizationTabLoading(false);
                  }, delay);
                });
              }
            }}
            disabled={refreshing || loading || (activeTab === "monetization" && (monetizationTabLoading || logsLoading || coinsGraphLoading || subsGraphLoading))}
            className="p-2.5 rounded-2xl border border-gray-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-40 shadow-2xs"
            title="Refresh Analytics Data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing || loading || (activeTab === "monetization" && (monetizationTabLoading || logsLoading || coinsGraphLoading || subsGraphLoading)) ? "animate-spin text-pink-600" : ""}`} />
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-2xl text-xs sm:text-sm font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 text-gray-400" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-8 max-w-[1650px] mx-auto">

        {/* Navigation Tabs (Option A + Dedicated Logs) & Timeline Filter */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Sub-Tabs Switcher */}
          <div className="inline-flex items-center p-1.5 bg-gray-100/90 rounded-2xl shadow-inner border border-gray-200/50 self-start">
            <button
              onClick={() => handleTabSwitch("engagement")}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 ${
                activeTab === "engagement"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <Users className={`w-4 h-4 ${activeTab === "engagement" ? "text-pink-600" : "text-gray-400"}`} />
              <span>User Growth & Engagement</span>
            </button>
            <button
              onClick={() => handleTabSwitch("monetization")}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 ${
                activeTab === "monetization"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <Coins className={`w-4 h-4 ${activeTab === "monetization" ? "text-amber-500" : "text-gray-400"}`} />
              <span>Revenue & Monetization</span>
              <span className="px-2 py-0.5 text-[10px] font-extrabold rounded-full bg-amber-100 text-amber-800 tracking-wide">
                CAD Base ($)
              </span>
            </button>
          </div>

          {/* Timeline Filter Strip */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-3 sm:p-4 flex items-center justify-between gap-4" ref={filterRef}>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider hidden sm:inline">Time Period:</span>
              <div className="relative">
                <button
                  onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
                  className="flex items-center gap-2.5 px-4 py-2 bg-white border border-gray-200 rounded-2xl text-xs sm:text-sm font-semibold text-gray-900 hover:bg-gray-50 shadow-2xs transition-colors"
                >
                  <Calendar className="w-4 h-4 text-pink-500" />
                  <span>{selectedDurationLabel}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {isTimeDropdownOpen && (
                  <div className="absolute right-0 lg:left-0 mt-2.5 w-60 bg-white rounded-2xl border border-gray-100 shadow-2xl z-30 py-2 text-xs sm:text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-150">
                    {DURATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => handleSelectTimeRange(opt.id)}
                        className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${timeRange === opt.id ? "text-pink-600 bg-pink-50/50 font-semibold" : "text-gray-700"
                          }`}
                      >
                        <span>{opt.label}</span>
                        {timeRange === opt.id && <span className="w-2 h-2 rounded-full bg-pink-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-xs text-gray-400 font-medium flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="hidden sm:inline">Auto-refreshes every 15 min</span>
            </div>
          </div>
        </div>

        {activeTab === "engagement" ? (
          <>
            {/* Top 6 Key Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
          {/* Total Users */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Users</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : totalUsers.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{totalUsersGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* New Users */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <UserPlus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">New Users</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : newUsers.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{newUsersGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Activity className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Users</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : activeUsers7d.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{activeUsers7dGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Matches */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
              <Heart className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Matches</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : matches7d.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{matches7dGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Messages Sent */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Messages</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : messagesSent7d.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{messagesSent7dGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Reports */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <Flag className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Reports</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : reports7d.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-rose-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{reports7dGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>
        </div>

        {/* Main Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">

          {/* User Growth Line Chart */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex items-center gap-3.5">
              <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-cyan-50">
                <UserPlus className="w-4 h-4 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">User Growth — {selectedDurationLabel}</h2>
                <p className="text-xs text-gray-400">Daily signups trend from real database</p>
              </div>
            </div>

            {loading ? (
              <div className="h-64 bg-gray-50/70 rounded-2xl animate-pulse flex items-center justify-center text-xs text-gray-400 font-medium">Updating data...</div>
            ) : growthChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400 font-medium">No user signups recorded in database</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={growthChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="current"
                    name="new users"
                    stroke="#ec4899"
                    strokeWidth={2.8}
                    dot={false}
                    activeDot={{ r: 6, fill: "#ec4899" }}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* User Activity Stacked Bar Chart */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">User Activity</h2>
                <p className="text-xs text-gray-400">Active users over time</p>
              </div>
              <div className="flex items-center gap-4 text-xs font-medium text-gray-600">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                  <span>Active</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-purple-300" />
                  <span>Inactive</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-pink-400" />
                  <span>New</span>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="h-64 bg-gray-50/70 rounded-2xl animate-pulse flex items-center justify-center text-xs text-gray-400 font-medium">Updating data...</div>
            ) : activityChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400 font-medium">No activity recorded</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={activityChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "transparent" }} content={<CustomTooltip />} />
                  <Bar dataKey="active" name="active" stackId="a" fill="#7c3aed" barSize={24} />
                  <Bar dataKey="inactive" name="inactive" stackId="a" fill="#c4b5fd" />
                  <Bar dataKey="newUsers" name="newUsers" stackId="a" fill="#f472b6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Demographics, Top Cities & Engagement Overview Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">

          {/* User Demographics Donut Charts with Animated Tooltips */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 flex flex-col justify-between space-y-6">
            <div>
              <h2 className="text-base font-bold text-gray-900">User Demographics</h2>
              <p className="text-xs text-gray-400 mb-5">Hover slice to view stats popover</p>

              <div className="grid grid-cols-2 gap-6 items-center">
                {/* Age Donut */}
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-700 mb-3">Age Groups</p>
                  {ageData.length === 0 ? (
                    <div className="h-40 flex items-center justify-center text-xs text-gray-400">No DOB data</div>
                  ) : (
                    <>
                      <div className="h-40 relative flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Tooltip content={<CustomDemographicsTooltip />} />
                            <Pie data={ageData} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="percentage" paddingAngle={2}>
                              {ageData.map((e, i) => (
                                <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[11px] text-gray-600 mt-3 text-left font-medium">
                        {ageData.map((a, i) => (
                          <div key={a.label} className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: AGE_COLORS[i % AGE_COLORS.length] }} />
                            <span>{a.label}: <b>{a.percentage}%</b></span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Gender Donut */}
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-700 mb-3">Gender</p>
                  {genderData.length === 0 ? (
                    <div className="h-40 flex items-center justify-center text-xs text-gray-400">No gender data</div>
                  ) : (
                    <>
                      <div className="h-40 relative flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Tooltip content={<CustomDemographicsTooltip />} />
                            <Pie data={genderData} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="percentage" paddingAngle={2}>
                              {genderData.map((e, i) => (
                                <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-1.5 text-[11px] text-gray-600 mt-3 text-left font-medium">
                        {genderData.map((g, i) => (
                          <div key={g.label} className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GENDER_COLORS[i % GENDER_COLORS.length] }} />
                              <span>{g.label}</span>
                            </div>
                            <span className="font-bold">{g.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Redesigned Spacious Top Cities Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 flex flex-col justify-between space-y-6">
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-base font-bold text-gray-900">Top Cities</h2>
                  <p className="text-xs text-gray-400">Geographic active user concentration</p>
                </div>
                <MapPin className="w-5 h-5 text-purple-500" />
              </div>

              {topCities.length === 0 ? (
                <div className="py-12 flex items-center justify-center text-xs text-gray-400 font-medium">No city data available</div>
              ) : (
                <div className="space-y-4">
                  {topCities.map((c, idx) => (
                    <div key={c.city + idx} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs font-medium">
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 rounded-lg bg-purple-50 text-purple-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            #{idx + 1}
                          </span>
                          <span className="text-gray-900 font-bold text-sm">{c.city}</span>
                          <span className="text-gray-400 text-xs font-medium">({c.country})</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <span className="tabular-nums font-bold text-gray-900 text-sm">{c.count?.toLocaleString()}</span>
                          <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                            {c.percentage}%
                          </span>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-gray-100/80 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, (c.count / (topCities[0]?.count || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowCitiesDrawer(true)}
              className="w-full py-3 bg-purple-50/70 border border-purple-100 rounded-2xl text-xs sm:text-sm font-semibold text-purple-700 hover:bg-purple-100/70 transition-colors shadow-2xs"
            >
              View All Cities
            </button>
          </div>

          {/* Engagement Overview Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 flex flex-col justify-between space-y-6">
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-1">Engagement Overview</h2>
              <p className="text-xs text-gray-400 mb-5">Key engagement metrics</p>

              <div className="space-y-3.5">
                <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                  <div className="flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Eye className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Profile Views</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-base font-bold text-gray-900">{engagement.profileViews7d?.toLocaleString()}</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{engagement.profileViewsGrowth}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                  <div className="flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center">
                      <Heart className="w-4 h-4 text-rose-500" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Likes Sent</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-base font-bold text-gray-900">{engagement.likesSent7d?.toLocaleString()}</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{engagement.likesSentGrowth}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2.5 border-b border-gray-50">
                  <div className="flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Matches</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-base font-bold text-gray-900">{engagement.matches7d?.toLocaleString()}</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{engagement.matchesGrowth}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                      <MessageSquare className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">Messages Sent</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-base font-bold text-gray-900">{engagement.messagesSent7d?.toLocaleString()}</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{engagement.messagesSentGrowth}%</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowEngagementDrawer(true)}
              className="w-full py-3 bg-purple-50/70 border border-purple-100 rounded-2xl text-xs sm:text-sm font-semibold text-purple-700 hover:bg-purple-100/70 transition-colors inline-flex items-center justify-center gap-2 shadow-2xs"
            >
              <span>View Full Engagement Report</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </>
    ) : isMonetizationInitialLoading ? (
      <MonetizationLoadingSkeleton />
    ) : (
      /* ========================================================= */
      /* REVENUE & MONETIZATION TAB VIEW                           */
      /* ========================================================= */
      <div className="space-y-8 animate-in fade-in duration-200">
        {/* Top 6 Monetization & Economy KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
          {/* Total Revenue */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Revenue</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                  Base CAD
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : `CA$ ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-medium">
                Normalized base pricing
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{totalRevenueGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Coin Purchases Revenue */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Coins className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Coin Purchases</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                  Base CAD
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : `CA$ ${coinRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-medium">
                +{totalCoinsPurchased.toLocaleString()} coins bought
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-amber-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{coinRevenueGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Subscription Revenue */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <Crown className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sub Revenue</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                  Base CAD
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : `CA$ ${subscriptionRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-medium">
                Standard tier pricing
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-purple-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{subscriptionRevenueGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
            </div>
          </div>

          {/* Active Subscribers */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Subs</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : activeSubscribers.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-medium">
                Active VIP accounts
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-blue-600">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{activeSubscribersGrowth}%</span>
              <span className="text-gray-400 font-normal ml-0.5">active</span>
            </div>
          </div>

          {/* Coins Burned (Spent) */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
              <Flame className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Coins Burned</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">
                  Base CAD
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : `CA$ ${totalCoinsBurnedCad.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 font-medium">
                {totalCoinsBurned.toLocaleString()} coins spent
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-rose-600 truncate">
              <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{totalCoinsBurnedGrowth >= 0 ? `+${totalCoinsBurnedGrowth}%` : `${totalCoinsBurnedGrowth}%`}</span>
              <span className="text-gray-400 font-normal ml-0.5">burn rate</span>
            </div>
          </div>

          {/* Paid Conversion & ARPU */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-5 sm:p-6 flex flex-col justify-between transition-all hover:shadow-md">
            <div className="w-10 h-10 rounded-2xl bg-cyan-50 flex items-center justify-center mb-4">
              <Sparkles className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Conversion</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums mt-1">
                {loading ? "..." : `${freeToPaidRate}%`}
              </p>
            </div>
            <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-cyan-600 truncate">
              <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">ARPU: CA$ {arpu} • {payingUsersCount} paying</span>
            </div>
          </div>
        </div>

        {/* Revenue Streams Trend (AreaChart - Global Timeline Overview) */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <h2 className="text-base font-bold text-gray-900">Revenue Streams Trend</h2>
              </div>
              <p className="text-xs text-gray-400 mt-1">Daily income from Coin Purchases vs Recurring Subscriptions (CAD Base)</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-emerald-600">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Total
              </span>
              <span className="flex items-center gap-1.5 text-amber-600">
                <span className="w-2 h-2 rounded-full bg-amber-500" /> Coins
              </span>
              <span className="flex items-center gap-1.5 text-purple-600">
                <span className="w-2 h-2 rounded-full bg-purple-500" /> Subscriptions
              </span>
            </div>
          </div>

          <div className="h-[280px] w-full">
            {loading ? (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-xs text-gray-400 bg-gray-50/50 rounded-2xl animate-pulse">
                <RefreshCw className="w-5 h-5 animate-spin text-emerald-600" />
                <span className="font-medium">Loading revenue stream trend...</span>
              </div>
            ) : revenueTrendSeries.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-400">
                No revenue recorded in this time period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrendSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="totalRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="coinRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="subRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `CA$${v}`} />
                  <Tooltip content={<CustomCurrencyTooltip />} />
                  <Area type="monotone" dataKey="totalRevenue" name="Total Revenue" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#totalRevGrad)" />
                  <Area type="monotone" dataKey="coinRevenue" name="Coin Sales" stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#coinRevGrad)" />
                  <Area type="monotone" dataKey="subRevenue" name="Subscription" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#subRevGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Dedicated Individual Graphs: Coins & Subscriptions with Independent Time Filters (Task 5) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Coins Graph Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <h2 className="text-base font-bold text-gray-900">Coin Economy: Mint vs. Burn</h2>
                  {coinsGraphLoading && (
                    <RefreshCw className="w-3.5 h-3.5 text-amber-500 animate-spin" />
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Coins minted vs burned into circulation (Base CAD: CA$ 0.125 / coin)
                </p>
              </div>

              {/* Individual Time Filter Toolbar */}
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <div className="inline-flex items-center p-1 bg-gray-100/80 rounded-xl border border-gray-200/50">
                  {INDIVIDUAL_GRAPH_TIME_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handleCoinsTimeRangeChange(opt.id)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all duration-150 ${
                        coinsTimeRange === opt.id
                          ? "bg-white text-gray-900 shadow-xs"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-50">
              <div className="flex items-center gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1.5 text-emerald-600">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Minted
                </span>
                <span className="flex items-center gap-1.5 text-rose-500">
                  <span className="w-2 h-2 rounded-full bg-rose-500" /> Burned
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/50">
                  +{activeCoinsPurchased.toLocaleString()} (CA$ {activeCoinsPurchasedCad.toFixed(2)})
                </span>
                <span className="font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200/50">
                  -{activeCoinsBurned.toLocaleString()} (CA$ {activeCoinsBurnedCad.toFixed(2)})
                </span>
              </div>
            </div>

            <div className="h-[320px] w-full relative">
              {coinsGraphLoading && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-2xl">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white shadow-md rounded-xl text-xs font-semibold text-amber-600 border border-amber-100">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Updating {coinsTimeRange.toUpperCase()}...</span>
                  </div>
                </div>
              )}
              {activeCoinsSeries.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No coin activity recorded in this time period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={activeCoinsSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomCoinsTooltip />} />
                    <Bar dataKey="purchased" name="Coins Purchased" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="burned" name="Coins Burned" fill="#f43f5e" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Subscription Graph Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                  <h2 className="text-base font-bold text-gray-900">Subscription Trends & Growth</h2>
                  {subsGraphLoading && (
                    <RefreshCw className="w-3.5 h-3.5 text-purple-500 animate-spin" />
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Recurring VIP subscription revenue (Base CAD) & member signups
                </p>
              </div>

              {/* Individual Time Filter Toolbar */}
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <div className="inline-flex items-center p-1 bg-gray-100/80 rounded-xl border border-gray-200/50">
                  {INDIVIDUAL_GRAPH_TIME_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handleSubsTimeRangeChange(opt.id)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all duration-150 ${
                        subsTimeRange === opt.id
                          ? "bg-white text-gray-900 shadow-xs"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-50">
              <div className="flex items-center gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1.5 text-purple-600">
                  <span className="w-2 h-2 rounded-full bg-purple-500" /> Revenue (CAD)
                </span>
                <span className="flex items-center gap-1.5 text-indigo-600">
                  <span className="w-2 h-2 rounded-full bg-indigo-500" /> New VIPs
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-200/50">
                  CA$ {Number(activeSubsRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-200/50">
                  +{activeNewSubscribers} new
                </span>
                <span className="font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md border border-gray-200/50">
                  {activeTotalSubscribers} active VIPs
                </span>
              </div>
            </div>

            <div className="h-[320px] w-full relative">
              {subsGraphLoading && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-2xl">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white shadow-md rounded-xl text-xs font-semibold text-purple-600 border border-purple-100">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Updating {subsTimeRange.toUpperCase()}...</span>
                  </div>
                </div>
              )}
              {activeSubsSeries.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No subscription activity recorded in this time period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activeSubsSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="subGraphGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `CA$${v}`} />
                    <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomSubscriptionTooltip />} />
                    <Area yAxisId="left" type="monotone" dataKey="subRevenue" name="Subscription Revenue" stroke="#8b5cf6" strokeWidth={2.5} fillOpacity={1} fill="url(#subGraphGrad)" />
                    <Line yAxisId="right" type="monotone" dataKey="newSubscribers" name="New VIP Signups" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Two Breakdown Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Coin Spend by Feature Breakdown */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">Coin Spend by Feature</h2>
                <p className="text-xs text-gray-400 mt-1">Actions consuming user coin balances</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200/60">
                  {totalCoinsBurned.toLocaleString()} Coins
                </span>
                <span className="text-xs font-bold px-2.5 py-1 bg-rose-50 text-rose-700 rounded-full border border-rose-200/60">
                  CA$ {totalCoinsBurnedCad.toFixed(2)} Burn
                </span>
              </div>
            </div>

            {coinSpendBreakdown.length === 0 ? (
              <div className="h-[240px] flex items-center justify-center text-xs text-gray-400">
                No coin feature transactions recorded yet
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                {/* Donut Chart */}
                <div className="h-[220px] w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={coinSpendBreakdown}
                        dataKey="coinsSpent"
                        nameKey="feature"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={4}
                      >
                        {coinSpendBreakdown.map((entry, index) => (
                          <Cell key={`feat-${index}`} fill={FEATURE_PALETTE[index % FEATURE_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val, name) => [
                          `${Number(val).toLocaleString()} coins (CA$ ${(Number(val) * 0.125).toFixed(2)})`,
                          name
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* List with Progress Bars */}
                <div className="space-y-3">
                  {coinSpendBreakdown.slice(0, 5).map((item, index) => (
                    <div key={item.feature + index} className="space-y-1">
                      <div className="flex items-center justify-between text-xs font-semibold">
                        <span className="flex items-center gap-2 text-gray-800">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: FEATURE_PALETTE[index % FEATURE_PALETTE.length] }}
                          />
                          <span>{item.feature}</span>
                        </span>
                        <div className="text-right">
                          <span className="text-gray-900 font-bold tabular-nums">
                            {Number(item.coinsSpent || 0).toLocaleString()}
                          </span>
                          <span className="text-gray-500 font-medium ml-1.5 tabular-nums text-[11px]">
                            • CA$ {(item.cadValue ?? (Number(item.coinsSpent || 0) * 0.125)).toFixed(2)}
                          </span>
                          <span className="text-gray-400 font-normal ml-1">({item.percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${item.percentage}%`,
                            backgroundColor: FEATURE_PALETTE[index % FEATURE_PALETTE.length]
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Subscription Plans & Health */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">Subscription Plans & Health</h2>
                <p className="text-xs text-gray-400 mt-1">Tier distribution and membership health</p>
              </div>
              <span className="text-xs font-bold px-3 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-200/60">
                {activeSubscribers} Active Members
              </span>
            </div>

            {subscriptionPlanBreakdown.length === 0 ? (
              <div className="h-[240px] flex items-center justify-center text-xs text-gray-400">
                No active subscriptions recorded in this period
              </div>
            ) : (
              <div className="space-y-5">
                {/* Plan Tier Distribution Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {subscriptionPlanBreakdown.map((plan, index) => (
                    <div
                      key={plan.planCode + index}
                      className="p-4 rounded-2xl border border-gray-100 bg-gray-50/50 flex flex-col justify-between space-y-2 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-900">{plan.planName}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          {plan.percentage}% share
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-lg font-bold text-gray-900">{plan.count} subscribers</span>
                        <span className="text-xs font-bold text-emerald-600 tabular-nums">
                          CA$ {Number(plan.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Status Breakdown Pills */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 mb-3">Subscription Status Breakdown:</p>
                  <div className="flex flex-wrap gap-2">
                    {subscriptionStatusBreakdown.map((statusItem, idx) => {
                      const statusColors = {
                        ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200/60",
                        PENDING: "bg-amber-50 text-amber-700 border-amber-200/60",
                        EXPIRED: "bg-gray-100 text-gray-700 border-gray-200",
                        CANCELLED: "bg-rose-50 text-rose-700 border-rose-200/60",
                      };
                      const colorClass = statusColors[statusItem.status] || "bg-gray-50 text-gray-600 border-gray-200";
                      return (
                        <div
                          key={statusItem.status + idx}
                          className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-2 ${colorClass}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          <span>{statusItem.status}</span>
                          <span className="font-bold tabular-nums">({statusItem.count})</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 3: Live Coin & Subscription Purchase & Transaction Logs */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xs p-6 sm:p-7 space-y-6">
          {/* Header & Category Pills Toolbar */}
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 pb-4 border-b border-gray-100">
            <div>
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-600" />
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Recent Transactions & Purchases</h2>
                <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-purple-50 text-purple-700 border border-purple-200/60">
                  {logsData.totalElements || 0} Total Events
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Live audit ledger tracking coin purchases, VIP membership subscriptions, and premium feature debits
              </p>
            </div>

            {/* Category Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 xl:pb-0">
              {[
                { id: "ALL", label: "All Logs" },
                { id: "COIN_PURCHASE", label: "🪙 Coin Purchases" },
                { id: "SUBSCRIPTION", label: "👑 Subscriptions" },
                { id: "COIN_SPENT", label: "🔥 Coins Spent" },
              ].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setLogsCategory(cat.id);
                    setLogsPage(0);
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-150 ${
                    logsCategory === cat.id
                      ? "bg-purple-600 text-white shadow-xs ring-2 ring-purple-600/20"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200/80"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mini Summary Banner for Logs */}
          {logsData?.summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50/80 p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400">Ledger Revenue</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    CA$ {(logsData.summary.totalRevenueCad || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400">Ledger Burn</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    CA$ {(logsData.summary.totalCoinsBurnedCad || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <Coins className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400">Bought / Burned</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    +{(logsData.summary.totalCoinsPurchased || 0).toLocaleString()} / -{(logsData.summary.totalCoinsBurned || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400">Orders & Events</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    {(logsData.summary.coinPurchasesCount || 0) + (logsData.summary.subscriptionOrdersCount || 0)} orders • {(logsData.summary.coinSpendsCount || 0)} burns
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar & Country Filter Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  placeholder="Search user name, email, plan, or TX ID..."
                  className="w-full pl-10 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs sm:text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                />
                {logsSearch && (
                  <button
                    onClick={() => {
                      setLogsSearch("");
                      setDebouncedSearch("");
                      setLogsPage(0);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Custom Country Dropdown Filter */}
              <div className="relative min-w-[190px]" ref={countryDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsCountryDropdownOpen(!isCountryDropdownOpen);
                    setCountryFilterSearch("");
                  }}
                  className={`w-full inline-flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-2xl text-xs font-bold border shadow-2xs transition-all ${
                    logsCountry !== "ALL"
                      ? "bg-purple-50/90 border-purple-300 text-purple-900 ring-2 ring-purple-500/20"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-sm flex-shrink-0">{getCountryFlag(logsCountry)}</span>
                    <span className="truncate">{logsCountry === "ALL" ? "All Countries" : logsCountry}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {logsCountry !== "ALL" && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogsCountry("ALL");
                          setLogsPage(0);
                        }}
                        className="p-0.5 rounded-full hover:bg-purple-200/60 text-purple-600 transition-colors cursor-pointer"
                        title="Reset to All Countries"
                      >
                        <X className="w-3 h-3" />
                      </span>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-150 ${isCountryDropdownOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {isCountryDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 w-72 max-h-84 bg-white rounded-2xl border border-gray-100 shadow-2xl z-50 text-xs font-medium flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                    {/* Header & Search */}
                    <div className="p-2.5 border-b border-gray-100 bg-gray-50/70 space-y-2">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">
                          Filter by Country ({availableCountriesList.length})
                        </span>
                        {activeTransactionCountries.length > 0 && (
                          <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-200/50">
                            {activeTransactionCountries.length} Active
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countryFilterSearch}
                          onChange={(e) => setCountryFilterSearch(e.target.value)}
                          className="w-full pl-8 pr-7 py-1.5 bg-white border border-gray-200 rounded-xl text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                          autoFocus
                        />
                        {countryFilterSearch && (
                          <button
                            type="button"
                            onClick={() => setCountryFilterSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* List Content */}
                    <div className="overflow-y-auto max-h-56 py-1.5 divide-y divide-gray-50">
                      {/* Worldwide Option */}
                      {!countryFilterSearch && (
                        <div className="pb-1">
                          <button
                            type="button"
                            onClick={() => {
                              setLogsCountry("ALL");
                              setLogsPage(0);
                              setIsCountryDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3.5 py-2 hover:bg-purple-50/80 transition-colors flex items-center justify-between ${
                              logsCountry === "ALL" ? "text-purple-700 bg-purple-50 font-bold" : "text-gray-700"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-base">🌐</span>
                              <span>All Countries (Worldwide)</span>
                            </span>
                            {logsCountry === "ALL" && <span className="w-2 h-2 rounded-full bg-purple-600" />}
                          </button>
                        </div>
                      )}

                      {/* Active Countries with Recorded Transactions */}
                      {!countryFilterSearch && activeTransactionCountries.length > 0 && (
                        <div className="py-1">
                          <div className="px-3.5 py-1 text-[9px] uppercase font-bold text-purple-600 tracking-wider">
                            Markets with Activity
                          </div>
                          {activeTransactionCountries.map((c) => (
                            <button
                              key={`active-${c}`}
                              type="button"
                              onClick={() => {
                                setLogsCountry(c);
                                setLogsPage(0);
                                setIsCountryDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3.5 py-1.5 hover:bg-purple-50/80 transition-colors flex items-center justify-between ${
                                logsCountry === c ? "text-purple-700 bg-purple-50 font-bold" : "text-gray-700"
                              }`}
                            >
                              <span className="flex items-center gap-2 truncate">
                                <span className="text-sm">{getCountryFlag(c)}</span>
                                <span className="truncate">{c}</span>
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold text-purple-700 bg-purple-100/70 px-1.5 py-0.5 rounded-full">
                                  Active
                                </span>
                                {logsCountry === c && <span className="w-2 h-2 rounded-full bg-purple-600" />}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* All Available Countries */}
                      <div className="py-1">
                        {!countryFilterSearch && activeTransactionCountries.length > 0 && (
                          <div className="px-3.5 py-1 text-[9px] uppercase font-bold text-gray-400 tracking-wider">
                            All Countries
                          </div>
                        )}
                        {displayedCountries.length === 0 ? (
                          <div className="px-4 py-6 text-center text-gray-400 text-xs">
                            No country matching &ldquo;{countryFilterSearch}&rdquo;
                          </div>
                        ) : (
                          displayedCountries.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => {
                                setLogsCountry(c);
                                setLogsPage(0);
                                setIsCountryDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3.5 py-1.5 hover:bg-purple-50/80 transition-colors flex items-center justify-between ${
                                logsCountry === c ? "text-purple-700 bg-purple-50 font-bold" : "text-gray-700"
                              }`}
                            >
                              <span className="flex items-center gap-2 truncate">
                                <span className="text-sm">{getCountryFlag(c)}</span>
                                <span className="truncate">{c}</span>
                              </span>
                              {logsCountry === c && <span className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0" />}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 self-end md:self-auto">
              {logsCountry !== "ALL" && (
                <button
                  type="button"
                  onClick={() => {
                    setLogsCountry("ALL");
                    setLogsPage(0);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100/80 hover:bg-purple-200/80 text-purple-800 text-xs font-bold rounded-xl transition-colors shadow-2xs"
                  title="Clear Country Filter"
                >
                  <span>{getCountryFlag(logsCountry)} {logsCountry}</span>
                  <X className="w-3.5 h-3.5 text-purple-600" />
                </button>
              )}

              {/* Custom Rows Dropdown */}
              <div className="relative" ref={rowsDropdownRef}>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                  <span className="hidden sm:inline">Rows:</span>
                  <button
                    type="button"
                    onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800 shadow-2xs hover:bg-gray-50 transition-colors"
                  >
                    <span>{logsSize}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-150 ${isRowsDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>

                {isRowsDropdownOpen && (
                  <div className="absolute right-0 bottom-full sm:bottom-auto sm:top-full mt-2 w-32 bg-white rounded-2xl border border-gray-100 shadow-xl py-1.5 z-40 text-xs font-semibold animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-1 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                      Page Size
                    </div>
                    {[10, 15, 25, 50].map((sizeOption) => (
                      <button
                        key={sizeOption}
                        type="button"
                        onClick={() => {
                          setLogsSize(sizeOption);
                          setLogsPage(0);
                          setIsRowsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3.5 py-2 hover:bg-purple-50 transition-colors flex items-center justify-between ${
                          logsSize === sizeOption
                            ? "text-purple-600 bg-purple-50/60 font-bold"
                            : "text-gray-700 font-medium"
                        }`}
                      >
                        <span>{sizeOption} rows</span>
                        {logsSize === sizeOption && (
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => fetchLogs(logsPage, logsSize, logsCategory, debouncedSearch, timeRange, customStart, customEnd, logsCountry)}
                disabled={logsLoading}
                className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-40 shadow-2xs"
                title="Reload Ledger"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? "animate-spin text-purple-600" : ""}`} />
              </button>
            </div>
          </div>

          {/* Table Container */}
          {logsLoading ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 uppercase tracking-wider font-semibold">
                    <th className="pb-3 pl-2">User</th>
                    <th className="pb-3">Action / Plan</th>
                    <th className="pb-3 text-right">Coins / Units</th>
                    <th className="pb-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Base Price <span className="text-[10px] font-extrabold text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded">CAD</span>
                      </span>
                    </th>
                    <th className="pb-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Amount Paid <span className="text-[10px] font-extrabold text-purple-700 bg-purple-100/80 px-1.5 py-0.5 rounded">Local</span>
                      </span>
                    </th>
                    <th className="pb-3 text-right">Balance After</th>
                    <th className="pb-3 text-center">Status</th>
                    <th className="pb-3 text-right pr-2">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {[...Array(logsSize > 8 ? 8 : logsSize || 5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="py-3.5 pl-2 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-3.5 w-28 bg-gray-200 rounded-md" />
                          <div className="h-2.5 w-36 bg-gray-100 rounded-md" />
                        </div>
                      </td>
                      <td className="py-3.5">
                        <div className="h-6 w-24 bg-gray-100 rounded-full" />
                      </td>
                      <td className="py-3.5 text-right">
                        <div className="h-4 w-16 bg-gray-100 rounded-md ml-auto" />
                      </td>
                      <td className="py-3.5 text-right">
                        <div className="h-5 w-20 bg-gray-100 rounded-lg ml-auto" />
                      </td>
                      <td className="py-3.5 text-right">
                        <div className="h-5 w-20 bg-gray-100 rounded-md ml-auto" />
                      </td>
                      <td className="py-3.5 text-right">
                        <div className="h-4 w-16 bg-gray-100 rounded-md ml-auto" />
                      </td>
                      <td className="py-3.5 text-center">
                        <div className="h-5 w-16 bg-gray-100 rounded-full mx-auto" />
                      </td>
                      <td className="py-3.5 text-right pr-2">
                        <div className="h-4 w-20 bg-gray-100 rounded-md ml-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : logsData.items.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto text-gray-400">
                <Receipt className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold text-gray-700">No transactions recorded</p>
              <p className="text-xs text-gray-400 max-w-sm mx-auto">
                No coin purchases, subscriptions, or debits matched your category and search criteria.
              </p>
              {(logsSearch || logsCategory !== "ALL" || logsCountry !== "ALL") && (
                <button
                  onClick={() => {
                    setLogsSearch("");
                    setDebouncedSearch("");
                    setLogsCategory("ALL");
                    setLogsCountry("ALL");
                    setLogsPage(0);
                  }}
                  className="mt-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-bold rounded-xl transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 uppercase tracking-wider font-semibold">
                    <th className="pb-3 pl-2">User</th>
                    <th className="pb-3">Action / Plan</th>
                    <th className="pb-3 text-right">Coins / Units</th>
                    <th className="pb-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Base Price <span className="text-[10px] font-extrabold text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded">CAD</span>
                      </span>
                    </th>
                    <th className="pb-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Amount Paid <span className="text-[10px] font-extrabold text-purple-700 bg-purple-100/80 px-1.5 py-0.5 rounded">Local</span>
                      </span>
                    </th>
                    <th className="pb-3 text-right">Balance After</th>
                    <th className="pb-3 text-center">Status</th>
                    <th className="pb-3 text-right pr-2">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logsData.items.map((item, idx) => {
                    const isSubscription = item.category === "SUBSCRIPTION" || (item.type || "").startsWith("SUB_");
                    const isPurchase = item.category === "COIN_PURCHASE" || (item.type || "").toUpperCase() === "PURCHASE";
                    const isDebit = item.category === "COIN_SPENT" || Number(item.amount || 0) < 0;

                    const basePrice = item.basePriceCad != null ? Number(item.basePriceCad) : null;
                    const pricePaid = item.pricePaid != null ? Number(item.pricePaid) : (isPurchase || isSubscription ? basePrice : null);
                    const currencyCode = (item.currency || "CAD").toUpperCase();
                    const currSymbol = formatCurrencySymbol(currencyCode);

                    return (
                      <tr key={item.id || idx} className="hover:bg-purple-50/20 transition-colors">
                        {/* User Cell with deep link to Profile */}
                        <td
                          className="py-3.5 pl-2 flex items-center gap-3 cursor-pointer group"
                          title="Click to view full user profile"
                          onClick={() => {
                            if (item.userId) {
                              navigate(`/users?userId=${item.userId}`, { state: { from: "/analytics", email: item.userEmail } });
                            } else if (item.userEmail) {
                              navigate(`/users?search=${encodeURIComponent(item.userEmail)}`, { state: { from: "/analytics", email: item.userEmail } });
                            }
                          }}
                        >
                          {item.photoUrl ? (
                            <img
                              src={item.photoUrl}
                              alt={item.userName || "User"}
                              className="w-9 h-9 rounded-full object-cover group-hover:ring-2 group-hover:ring-purple-400 transition-all flex-shrink-0"
                            />
                          ) : (
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 group-hover:ring-2 group-hover:ring-purple-400 transition-all ${
                              isSubscription
                                ? "bg-purple-100 text-purple-700"
                                : isPurchase
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-gray-100 text-gray-700"
                            }`}>
                              {(item.userName || item.userEmail || "U").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="truncate max-w-[200px]">
                            <p className="font-bold text-gray-900 truncate group-hover:text-purple-600 transition-colors flex items-center gap-1">
                              <span>{item.userName || "User"}</span>
                              <ExternalLink className="w-2.5 h-2.5 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </p>
                            <p className="text-gray-400 text-[11px] truncate">{item.userEmail}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="text-[10px] text-gray-400 font-mono">{item.id}</span>
                              {(() => {
                                const rowCountry = item.country || (
                                  currencyCode === "INR" ? "India" :
                                  currencyCode === "CAD" ? "Canada" :
                                  currencyCode === "USD" ? "United States" :
                                  currencyCode === "GBP" ? "United Kingdom" :
                                  currencyCode === "AUD" ? "Australia" :
                                  currencyCode === "EUR" ? "Germany" :
                                  currencyCode === "AED" ? "UAE" :
                                  null
                                );
                                if (!rowCountry) return null;
                                return (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200/70">
                                    <span>{getCountryFlag(rowCountry)}</span>
                                    <span className="capitalize">{rowCountry.toLowerCase().replace(/_/g, " ")}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </td>

                        {/* Action / Plan */}
                        <td className="py-3.5">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-[11px] ${
                              isSubscription
                                ? "bg-purple-50 text-purple-700 border border-purple-200/60"
                                : isPurchase
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200/50"
                                : isDebit
                                ? "bg-rose-50 text-rose-700 border border-rose-200/50"
                                : "bg-blue-50 text-blue-700 border border-blue-200/50"
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {item.title || item.type || "Transaction"}
                          </span>
                        </td>

                        {/* Coins / Units */}
                        <td className="py-3.5 text-right font-bold tabular-nums">
                          {isSubscription ? (
                            <span className="text-purple-700 font-semibold text-[11px] px-2 py-0.5 rounded-md bg-purple-50 border border-purple-100">
                              VIP Membership
                            </span>
                          ) : item.amount != null ? (
                            <span className={Number(item.amount) < 0 ? "text-rose-600" : "text-emerald-600 font-extrabold"}>
                              {Number(item.amount) > 0 ? `+${item.amount}` : item.amount} coins
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Base Price (CAD) */}
                        <td className="py-3.5 text-right tabular-nums">
                          {basePrice != null ? (
                            <div className="inline-flex flex-col items-end">
                              <span
                                className={`font-bold px-2 py-0.5 rounded-lg text-xs ${
                                  isDebit
                                    ? "bg-rose-50 text-rose-800 border border-rose-200/50"
                                    : "bg-amber-50 text-amber-900 border border-amber-200/50"
                                }`}
                              >
                                {isDebit ? "-" : ""}CA$ {basePrice.toFixed(2)}
                              </span>
                              <span className="text-[10px] text-gray-400 font-medium mt-0.5">
                                {isDebit ? "Burn Value" : "Base System"}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Amount Paid (Local Currency) */}
                        <td className="py-3.5 text-right tabular-nums">
                          {pricePaid != null ? (
                            <div className="inline-flex flex-col items-end">
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="font-bold text-gray-900 text-xs sm:text-sm tabular-nums">
                                  {currSymbol}{pricePaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span
                                  className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-md uppercase tracking-wider ${
                                    currencyCode === "CAD"
                                      ? "bg-gray-100 text-gray-700 border border-gray-200"
                                      : "bg-purple-100 text-purple-800 border border-purple-200"
                                  }`}
                                >
                                  {currencyCode}
                                </span>
                              </div>
                              {currencyCode !== "CAD" && basePrice != null ? (
                                <span className="text-[10px] text-gray-400 font-medium mt-0.5">
                                  ≈ CA$ {basePrice.toFixed(2)} base
                                </span>
                              ) : currencyCode === "CAD" ? (
                                <span className="text-[10px] text-emerald-600 font-medium mt-0.5">
                                  Direct CAD
                                </span>
                              ) : null}
                            </div>
                          ) : isDebit ? (
                            <div className="text-right">
                              <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200/40">
                                Coin Spend
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Balance After */}
                        <td className="py-3.5 text-right text-gray-600 tabular-nums font-medium">
                          {item.balanceAfter != null ? `${item.balanceAfter} coins` : "—"}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wider ${
                              ["ACTIVE", "PAID", "COMPLETED"].includes((item.status || "").toUpperCase())
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                                : ["PENDING"].includes((item.status || "").toUpperCase())
                                ? "bg-amber-50 text-amber-700 border border-amber-200/60"
                                : "bg-gray-100 text-gray-600 border border-gray-200"
                            }`}
                          >
                            {["ACTIVE", "PAID", "COMPLETED"].includes((item.status || "").toUpperCase()) && (
                              <CheckCircle2 className="w-2.5 h-2.5" />
                            )}
                            {item.status || "COMPLETED"}
                          </span>
                        </td>

                        {/* Date & Time */}
                        <td className="py-3.5 text-right text-gray-400 tabular-nums pr-2 whitespace-nowrap">
                          {item.createdAt ? (
                            <div>
                              <p className="font-semibold text-gray-700">
                                {new Date(item.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(item.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {logsData.totalPages > 1 && (
            <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-gray-500">
              <div>
                Showing{" "}
                <span className="font-bold text-gray-900">
                  {((logsData.page || 0) * (logsData.size || 15)) + 1}
                </span>{" "}
                to{" "}
                <span className="font-bold text-gray-900">
                  {Math.min(((logsData.page || 0) + 1) * (logsData.size || 15), logsData.totalElements || 0)}
                </span>{" "}
                of <span className="font-bold text-gray-900">{logsData.totalElements || 0}</span> logs
              </div>

              <div className="flex items-center gap-1.5 self-center sm:self-auto">
                <button
                  onClick={() => setLogsPage((p) => Math.max(0, p - 1))}
                  disabled={logsData.page <= 0 || logsLoading}
                  className="px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-white transition-colors flex items-center gap-1 font-semibold"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Prev</span>
                </button>

                {Array.from({ length: Math.min(5, logsData.totalPages) }, (_, i) => {
                  let pNum = i;
                  if (logsData.totalPages > 5 && logsData.page > 2) {
                    pNum = Math.min(logsData.page - 2 + i, logsData.totalPages - 1);
                  }
                  return (
                    <button
                      key={pNum}
                      onClick={() => setLogsPage(pNum)}
                      disabled={logsLoading}
                      className={`w-8 h-8 rounded-xl font-bold text-xs transition-all ${
                        logsData.page === pNum
                          ? "bg-purple-600 text-white shadow-xs"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {pNum + 1}
                    </button>
                  );
                })}

                <button
                  onClick={() => setLogsPage((p) => Math.min(logsData.totalPages - 1, p + 1))}
                  disabled={logsData.page >= logsData.totalPages - 1 || logsLoading}
                  className="px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-white transition-colors flex items-center gap-1 font-semibold"
                >
                  <span>Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

      </div>

      {/* Footer Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-100 px-6 sm:px-8 py-3.5 shadow-xs z-20 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-gray-500 font-medium">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-500" />
          <span>Analytics data updates automatically every 15 minutes.</span>
        </div>
        <div>
          <span>Last updated: </span>
          <span className="text-gray-900 font-semibold">
            {lastUpdated.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}{" "}
            {lastUpdated.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
          </span>
        </div>
      </div>

      {/* Custom Date Range Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 space-y-6 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Custom Date Range</h3>
                <p className="text-xs text-gray-400 mt-0.5">Select start and end dates</p>
              </div>
              <button onClick={() => setShowCustomModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-2xl text-xs font-semibold text-gray-900 outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-2xl text-xs font-semibold text-gray-900 outline-none focus:border-pink-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowCustomModal(false)}
                className="w-1/2 py-3 border border-gray-200 rounded-2xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStart || !customEnd}
                className="w-1/2 py-3 bg-pink-600 text-white rounded-2xl text-xs font-semibold shadow-xs hover:bg-pink-700 disabled:opacity-40"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redesigned Spacious Slide-over Drawer 1: View All Cities */}
      {showCitiesDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900/40 backdrop-blur-xs flex justify-end">
          <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-200">
            <div className="flex-1 flex flex-col min-h-0">
              <div className="px-7 py-6 border-b border-gray-100 flex items-center justify-between bg-white z-10 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">All Top Cities</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Full geographic active user distribution</p>
                </div>
                <button onClick={() => setShowCitiesDrawer(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100/70 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-7 space-y-5 flex-1 flex flex-col min-h-0">
                <div className="relative flex-shrink-0">
                  <Search className="w-4 h-4 absolute left-4 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search city or country..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-2xl text-xs sm:text-sm font-medium outline-none focus:border-purple-500 focus:bg-white transition-all"
                  />
                </div>

                <div className="flex-1 overflow-y-auto pr-3 space-y-3">
                  {filteredCities.length === 0 ? (
                    <div className="py-12 text-center text-xs text-gray-400 font-medium">No matching cities found</div>
                  ) : (
                    filteredCities.map((c, idx) => (
                      <div key={c.city + idx} className="p-4 bg-gray-50/60 hover:bg-purple-50/30 rounded-2xl border border-gray-100 transition-all flex items-center justify-between shadow-2xs">
                        <div className="flex items-center gap-3.5">
                          <span className="w-8 h-8 rounded-xl bg-purple-100/70 text-purple-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                            #{idx + 1}
                          </span>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{c.city}</p>
                            <p className="text-xs text-gray-400 font-medium mt-0.5">{c.country}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{c.count?.toLocaleString()} users</p>
                          <p className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full inline-block mt-1">
                            {c.percentage}% of total
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button onClick={() => setShowCitiesDrawer(false)} className="w-full py-3 bg-gray-900 text-white rounded-2xl text-xs sm:text-sm font-semibold hover:bg-gray-800 transition-colors shadow-xs">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over Drawer 2: Full Engagement Report */}
      {showEngagementDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900/40 backdrop-blur-xs flex justify-end">
          <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-200">
            <div className="flex-1 flex flex-col min-h-0">
              <div className="px-7 py-6 border-b border-gray-100 flex items-center justify-between bg-white z-10 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Full Engagement Report</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Detailed user interaction telemetry</p>
                </div>
                <button onClick={() => setShowEngagementDrawer(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100/70 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-7 space-y-6 flex-1 overflow-y-auto pr-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50/50 p-5 rounded-2xl border border-purple-100/80">
                    <p className="text-xs font-semibold text-purple-600">Profile Views</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{engagement.profileViews7d?.toLocaleString()}</p>
                  </div>
                  <div className="bg-rose-50/50 p-5 rounded-2xl border border-rose-100/80">
                    <p className="text-xs font-semibold text-rose-600">Likes Sent</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{engagement.likesSent7d?.toLocaleString()}</p>
                  </div>
                  <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-100/80">
                    <p className="text-xs font-semibold text-emerald-600">Matches Created</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{engagement.matches7d?.toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50/50 p-5 rounded-2xl border border-blue-100/80">
                    <p className="text-xs font-semibold text-blue-600">Messages Sent</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{engagement.messagesSent7d?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button onClick={() => setShowEngagementDrawer(false)} className="w-full py-3 bg-gray-900 text-white rounded-2xl text-xs sm:text-sm font-semibold hover:bg-gray-800 transition-colors shadow-xs">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
